import { memo, useEffect, useMemo, useRef, useState } from 'react'
import { AgGridReact } from 'ag-grid-react'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-material.css'
import { ColDef, RowNode } from 'ag-grid-community'
import { KTSVG, toAbsoluteUrl } from '../../../../_metronic/helpers'
import { agConfigSideBar, tableCellStyle } from '../../../../utils/AgGridConfig'
import Toast, { ToastType } from '../../../../utils/toast'
import { DeleteConfirmPopup } from '../../../../utils/DeleteConfirmPopup'
import {
  RemoveInvestorOfUserMutation,
  RemoveInvestorOfUserMutationVariables,
  useGetAllFundsOfUserQuery,
  useGetAllInvestorComapniesQuery,
  useGetAllUsersOfAccountQuery,
  useGetExistingCompaniesOfInvestorQuery,
  useRemoveInvestorOfUserMutation,
} from '../../../../generated/graphql'
import { graphqlRequestClient, queryClient } from '../../../../queries/client'
import React from 'react'
import AddFundModal from './AddFundForUser'

interface Fund {
  id: number
  name: string
  amount: string
  companies: Company[]
}

interface Company {
  id: number
  name: string
  industry: string
}

interface FundTableProps {
  selectedFundId: number
  selectedUserId: string
  onFundSelect: (fundId: number) => void
  onDataUpdate?: () => void
}

const FundNameRender = memo((props: any) => (
  <span
    className='cursor-pointer text-hover-primary'
    onClick={() => props.onFundSelect(props.data.id)}
  >
    {props.value}
  </span>
))
const FundTypeRender = memo((props: any) => (
  <span
    className='cursor-pointer text-hover-primary'
    onClick={() => props.onFundSelect(props.data.id)}
  >
    {props.value}
  </span>
))

const FundTable: React.FC<FundTableProps> = ({
  selectedFundId,
  selectedUserId: userId,
  onFundSelect,
  onDataUpdate,
}) => {
  const [loading, setLoading] = useState<boolean>(false)
  const [isColumnOpen, setIsColumnOpen] = useState(false)
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [isFilter, setIsFilter] = useState(false)
  const [selectedRows, setSelectedRows] = useState<Fund[]>([])
  const [showConfirmDelete, setShowConfirmDelete] = useState(false)
  const [selectedFunds, setSelectedFunds] = useState<Fund[] | null>(null)
  const singleFundName = selectedFunds && selectedFunds.length === 1 ? selectedFunds[0].name : ''
  const [funds, setFunds] = useState<any[]>([])
  const [isFundEnabled, setIsFundEnabled] = useState(false)
  const [rowCount, setRowCount] = useState(funds?.length)
  const [selectedUserId, setSelectedUserId] = useState<string>(userId)
  const [showAddFund, setShowAddFund] = useState(false)

  useEffect(() => {
    if (userId !== selectedUserId) {
      setSelectedUserId(userId)
      setIsFundEnabled(true)
    }
  }, [userId])

  useEffect(() => {
    if (userId) setIsFundEnabled(true)
  }, [userId])

  const gridApi = useRef<any>(null)

  const {
    data: fundsData,
    isLoading: isFundsLoading,
    isFetched: isFundsSuccess,
  } = useGetAllFundsOfUserQuery(
    graphqlRequestClient,
    { input: selectedUserId },
    {
      enabled: isFundEnabled && !!selectedUserId,
      staleTime: 0,
      cacheTime: 0,
      onSuccess: () => {
        setIsFundEnabled(false)
      },
    }
  )

  useEffect(() => {
    if (fundsData) setFunds(fundsData?.getAllFundsOfUser)
  }, [fundsData])

  useEffect(() => {
    setRowCount(funds.length)
  }, [funds])

  const removeInvestorMutation = useRemoveInvestorOfUserMutation<Error>(
    graphqlRequestClient,
    {
      onSuccess: (
        data: RemoveInvestorOfUserMutation,
        _variables: RemoveInvestorOfUserMutationVariables,
        _context: unknown
      ) => {
        const compKey = {
          input: Number(selectedUserId),
        }
        setIsFundEnabled(true)
        queryClient.invalidateQueries(useGetAllFundsOfUserQuery.getKey(compKey))
        queryClient.invalidateQueries(
          useGetExistingCompaniesOfInvestorQuery.getKey({
            input: { userId: selectedUserId, investorId: selectedFundId },
          })
        )

        Toast('Fund(s) removed successfully!', ToastType.success)
      },

      onError: (error: any) => {
        console.error('Error removing fund:', error)
        const errorMessage = error?.errors?.[0]?.message || error?.message

        if (errorMessage?.includes('Foreign key constraint failed')) {
          Toast(
            'Cannot remove fund: There are existing dependencies that must be removed first.',
            ToastType.error
          )
        } else if (errorMessage?.includes('role_id')) {
          Toast(
            'Cannot remove fund: Related roles must be handled first. Please contact administrator.',
            ToastType.error
          )
        } else {
          Toast('Failed to remove funds. Please try again.', ToastType.error)
        }
      },
    },
    {}
  )

  const openToolPanel = (key: any) => {
    if (key === 'columns') {
      if (gridApi.current) {
        if (!isColumnOpen) gridApi.current.api.openToolPanel(key)
        else gridApi.current.api.closeToolPanel()
        setIsColumnOpen((state) => !state)
        setIsFilterOpen(false)
      }
    } else if (key === 'filters') {
      if (gridApi.current) {
        if (!isFilterOpen) gridApi.current.api.openToolPanel(key)
        else gridApi.current.api.closeToolPanel()
        setIsFilterOpen((state) => !state)
        setIsColumnOpen(false)
      }
    }
  }

  const handleRemoveFund = () => {
    if (!selectedFunds || selectedFunds.length === 0) return

    setLoading(true)

    const processRemovals = async () => {
      try {
        for (const fund of selectedFunds) {
          await removeInvestorMutation.mutateAsync({
            input: {
              investorId: fund.id,
              userId: Number(selectedUserId),
            },
          })
        }
      } catch (error) {
        console.error('Error in removal process:', error)
      }
    }

    processRemovals().finally(() => {
      setSelectedRows([])
      setLoading(false)
      setShowConfirmDelete(false)
      setSelectedFunds(null)

      if (gridApi.current?.api) {
        gridApi.current.api.deselectAll()
      }
    })
  }

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      autoHeight: true,
      wrapHeaderText: true,
      suppressColumnVirtualisation: true,
      columnsMenuParams: {
        suppressColumnFilter: true,
      },
      filterParams: {
        buttons: ['reset'],
        maxNumConditions: 5,
      },
      minWidth: 150,
      filter: false,
      resizable: true,
      flex: 1,
    }),

    []
  )

  const componentsRegistry = useMemo(
    () => ({
      fundName: memo(FundNameRender),
      fundType: memo(FundTypeRender)
    }),
    []
  )

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: 'Fund Name',
        field: 'name',
        cellRenderer: 'fundName',
        cellRendererParams: { onFundSelect },
        cellStyle: {
          ...tableCellStyle,
        },
        initialWidth: 200,
        minWidth: 350,
        sortable: true,
        menuTabs: ['filterMenuTab'],
        tooltipField: 'name',
        filter: true,
        suppressQuickFilter: false,
      },
        {
        headerName: 'Fund Type',
        field: 'type',
        cellRenderer: 'fundType',
        cellRendererParams: { onFundSelect },
        cellStyle: {
          ...tableCellStyle,
        },
        hide: true,
        initialWidth: 200,
        minWidth: 350,
        sortable: true,
        menuTabs: ['filterMenuTab'],
        tooltipField: 'name',
        filter: true,
        suppressQuickFilter: false,
      },


      {
        headerName: '',
        field: 'actions',
        hide: false,
        width: 90,
        pinned: 'right',
        maxWidth: 90,
        filter: false,
        menuTabs: [],
        colId: 'action-column',
        suppressNavigable: true,
        suppressColumnsToolPanel: true,
        resizable: true,
        sortable: false,
        cellRenderer: (params: any) => (
          <i
            className='bi bi-trash3 text-danger bg-hover-light-danger p-2 px-3 rounded-circle cursor-pointer'
            style={{ fontSize: '18px', width: '18px', height: '18px' }}
            onClick={() => {
              setSelectedFunds([params.data])
              setShowConfirmDelete(true)
            }}
          />
        ),
      },
    ],
    [onFundSelect, funds]
  )

  const rowData = useMemo(
    () =>
      funds.map((fund) => ({
        id: fund.id,
        name: fund.name,
        amount: fund.amount,
        type: fund.type || ' - '
      })),
    [funds]
  )

  const handleSearch = (e: any) => {
    const filterValue = e.target.value
    setIsFilter(!!filterValue)
    if (gridApi.current) {
      gridApi.current.api.setQuickFilter(filterValue)
      gridApi.current.api.deselectAll()
      setSelectedRows([])
      const displayedRowCount = gridApi.current.api.getDisplayedRowCount()
      setRowCount(displayedRowCount)
    }
  }

  const onSelectionChanged = () => {
    const selectedNodes = gridApi.current?.api?.getSelectedNodes() || []
    const renderedNodes: RowNode[] = gridApi.current?.api?.getRenderedNodes() || []
    if (!isFilter) {
      const selectedRowData = selectedNodes.map((node: RowNode) => node.data)
      setSelectedRows(selectedRowData)
      return
    }
    const visibleSelectedRows = renderedNodes
      .filter((node: RowNode) => node.isSelected())
      .map((node: RowNode) => node.data)
    setSelectedRows(visibleSelectedRows)
  }

  const getFilteredData = () => {
    const filteredData: any[] = []
    gridApi.current?.api.forEachNodeAfterFilter((node: any) => {
      filteredData.push(node.data)
    })
    setRowCount(filteredData.length)
    if (funds.length !== filteredData.length) {
      setIsFilter(true)
    } else {
      setIsFilter(false)
    }
    return filteredData
  }

  return (
    <div className='p-0'>
      <div className='card'>
        <div className='d-flex flex-row justify-content-between align-items-center border-0 py-3 px-6'>
          <div>
            <h3 className='mb-1'>Funds</h3>
            {rowCount > 0 && (
              <p className='fs-7 text-gray-500 mb-0'>
                {rowCount} {rowCount > 1 ? 'Funds' : 'Fund'}
              </p>
            )}
          </div>
          <div className='d-flex flex-row align-items-center ms-auto gap-3'>
            {/* <button> add fund</button> */}
            {/* <button className='btn btn-primary h-38px' onClick={() => setShowAddFund(true)}>
              + Add Fund
            </button> */}
            <span
              className='d-flex flex-column border border-1 border-secondary-txt bg-transparent h-38px items-center w-full rounded'
              style={{
                boxShadow: '0px 3px 4px 0px rgba(0, 0, 0, 0.03)',
                border: '1px solid #F1F1F2',
                zIndex: 6,
              }}
            >
              <span className='d-flex flex-row align-items-center'>
                <KTSVG
                  path='media/icons/duotune/general/gen021.svg'
                  className='ps-4 svg-icon-2x text-start fs-9'
                  svgClassName='text-gray-700'
                />
                <input
                  type='search'
                  placeholder='Search by Fund Name'
                  className='form-control w-auto mw-200-px border-0 h-35px rounded'
                  onChange={handleSearch}
                />
              </span>
            </span>
            <button
              type='submit'
              value='Columns'
              onClick={() => openToolPanel('columns')}
              className='px-4 rounded border border-1 border-secondary-txt bg-transparent h-38px fw-normal text-hover-primary'
            >
              <KTSVG
                path='/media/icons/duotune/layouts/lay004.svg'
                className='svg-icon-3 text-secondary-txt me-1'
              />
              Columns
            </button>

            <button
              value='Filters'
              onClick={() => openToolPanel('filters')}
              className='px-4 rounded border border-1 border-secondary-txt bg-transparent h-38px fw-normal text-hover-primary'
            >
              <KTSVG
                path='/media/icons/duotune/general/gen031.svg'
                className='svg-icon-3 text-secondary-txt me-1'
              />
              Filter
            </button>
          </div>
        </div>

        <div className='card-body w-100 p-6 pt-0 ag-theme-material' style={{ height: '250px' }}>
          <AgGridReact
            sideBar={agConfigSideBar}
            onGridReady={(params) => {
              gridApi.current = params
            }}
            onFilterChanged={getFilteredData}
            components={componentsRegistry}
            alwaysShowHorizontalScroll
            alwaysShowVerticalScroll
            alwaysMultiSort
            animateRows
            defaultColDef={defaultColDef}
            rowData={rowData}
            columnDefs={columnDefs}
            domLayout='normal'
            pagination={false}
            rowSelection='multiple'
            rowMultiSelectWithClick
            onSelectionChanged={onSelectionChanged}
            tooltipShowDelay={50}
            tooltipInteraction
            rowClass='border-right-0 border-left-0 border-dashed'
            getRowStyle={(params) => ({
              background: params.rowIndex % 2 === 0 ? '#f8f8f8' : '#ffffff',
            })}
          />
        </div>

        {rowCount === 0 && (
          <div className='text-center mt-4'>
            <img
              alt='no funds'
              src={toAbsoluteUrl('/media/custom-images/emptyInvestorFunds.svg')}
              className='h-150px my-6'
            />
            <h5 className='text-gray-600'>No investor funds found for the selected user.</h5>
          </div>
        )}

        <DeleteConfirmPopup
          showModel={showConfirmDelete}
          setShowModel={setShowConfirmDelete}
          loading={loading}
          title='Delete Fund'
          description={`Are you sure you want to permanently delete "${singleFundName}"?`}
          notes='If you delete this fund, it will be removed from all associated companies and cannot be undone.'
          confirmText={`Type the fund name "${singleFundName}" to confirm`}
          currentValue={singleFundName}
          primaryButtonText='Delete'
          secondaryButtonText='Cancel'
          primaryAction={handleRemoveFund}
        />

        {showAddFund && (
          <AddFundModal
            userId={selectedUserId}
            onClose={() => setShowAddFund(false)}
            onSuccess={() => {
              setIsFundEnabled(true)
              setShowAddFund(false)
            }}
          />
        )}
      </div>
    </div>
  )
}

export default FundTable
